<script setup lang="ts">
import { logger } from '@/utils/logger';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { computed, ref, watch } from 'vue';
import dayjs from 'dayjs';
import type { HeatmapData, SelectOption } from 'naive-ui';
import { getUserActivityStatistics, type UserActivityStatistics } from '@/api';
import StatisticsBarChartCard from '@/components/admin/StatisticsBarChartCard.vue';
import StatisticsStackedBarChartCard, {
  type StackedSeries,
} from '@/components/admin/StatisticsStackedBarChartCard.vue';
import { buildStatisticsDateQuery, type StatisticsFiltersProps } from '@/utils/adminStatistics';
import { categoricalColor } from '@/utils/usageColors';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaSegmented from '@/components/design/base/GaiaSegmented.vue';

const props = defineProps<StatisticsFiltersProps>();
const message = useGaiaToast();

const data = ref<UserActivityStatistics | undefined>(undefined);

const selectedDay = ref<string | null>(null);

// Requests are weighted 0-3 by app area on the backend; `min_points`
// thresholds only the daily-active-users count against a user's highest
// request weight of the day.
const minPoints = ref<number>(0);

const minPointsOptions: SelectOption[] = [
  { label: '0 — Any activity (logged in)', value: 0 },
  { label: '1 — Chores or more', value: 1 },
  { label: '2 — Viewing data or more', value: 2 },
  { label: '3 — Productive work only', value: 3 },
];

type ActivityMetric = 'active_users' | 'peak_points' | 'total_points';

const metric = ref<ActivityMetric>('active_users');

const METRIC_LABELS: Record<ActivityMetric, string> = {
  active_users: 'Active Users',
  peak_points: 'Peak Points',
  total_points: 'Total Points',
};

const metricOptions: { label: string; value: ActivityMetric }[] = [
  { label: 'Active Users', value: 'active_users' },
  { label: 'Peak Points', value: 'peak_points' },
  { label: 'Total Points', value: 'total_points' },
];

const metricLabel = computed(() => METRIC_LABELS[metric.value]);

// The peak scale is fixed 0-3, so charts name the activity tier instead of
// showing the raw number — same wording as the threshold selector above.
const PEAK_POINT_LABELS = ['Any activity', 'Chores', 'Viewing data', 'Productive work'];

function formatPeakPoints(value: number): string {
  return PEAK_POINT_LABELS[value] ?? value.toLocaleString();
}

const peakValueTicks = {
  tickvals: PEAK_POINT_LABELS.map((_, index) => index),
  ticktext: PEAK_POINT_LABELS,
};

const metricValueFormatter = computed(() =>
  metric.value === 'peak_points' ? formatPeakPoints : undefined
);

interface DailyActivitySeries {
  daily_active_users: Record<string, number>;
  daily_peak_points: Record<string, number>;
  daily_total_points: Record<string, number>;
}

// Both the top-level payload and each org entry carry the same three series.
function metricSeries(source: DailyActivitySeries): Record<string, number> {
  switch (metric.value) {
    case 'active_users':
      return source.daily_active_users;
    case 'peak_points':
      return source.daily_peak_points;
    case 'total_points':
      return source.daily_total_points;
  }
}

const currentSeries = computed<Record<string, number | undefined>>(() =>
  data.value === undefined ? {} : metricSeries(data.value)
);

// GitHub-style full calendar year (Jan 1 – Dec 31), anchored to the year of
// the selected range's end (today's year when unset). Every day renders a box:
// days the backend reported get their value; the rest get 0 so they show as
// the neutral minimum shade, and the tooltip tells "no data" apart from a
// real 0.
const heatmapData = computed<HeatmapData>(() => {
  const series = currentSeries.value;
  const anchor = props.dateRange ? dayjs(props.dateRange[1]) : dayjs();
  const windowStart = anchor.startOf('year');
  const dayCount = anchor.endOf('year').diff(windowStart, 'day') + 1;

  return Array.from({ length: dayCount }, (_, index) => {
    const day = windowStart.add(index, 'day');
    const key = day.format('YYYY-MM-DD');
    return {
      timestamp: day.valueOf(),
      value: series[key] ?? 0,
    };
  });
});

function heatmapTooltip(timestamp: number): string {
  const day = dayjs(timestamp);
  const value: number | undefined = currentSeries.value[day.format('YYYY-MM-DD')];
  const date = day.format('MMM D, YYYY');
  if (value === undefined) return `${date} — no data`;
  if (metric.value === 'active_users') {
    return `${date} — ${value.toLocaleString()} active ${value === 1 ? 'user' : 'users'}`;
  }
  if (metric.value === 'peak_points') {
    return `${date} — peak: ${formatPeakPoints(value)}`;
  }
  return `${date} — ${metricLabel.value.toLowerCase()}: ${value.toLocaleString()}`;
}

// First cell of the heatmap grid: NHeatmap (first-day-of-week 0 = Monday) starts
// its calendar on the Monday of the earliest data point's week.
const heatmapGridStart = computed<dayjs.Dayjs | null>(() => {
  const first = heatmapData.value.at(0);
  if (!first) return null;
  const firstDay = dayjs(first.timestamp);
  return firstDay.subtract((firstDay.day() + 6) % 7, 'day');
});

// NHeatmap has no click event, so clicks are captured on a wrapper and the
// clicked cell's (weekday row, week column) position is mapped back to its date.
function onHeatmapClick(event: MouseEvent): void {
  const gridStart = heatmapGridStart.value;
  const rect = (event.target as HTMLElement).closest('.n-heatmap-rect');
  const cell = rect?.closest('td');
  const row = cell?.parentElement;
  if (!gridStart || !cell || !(row instanceof HTMLTableRowElement)) return;

  const weekIndex = cell.cellIndex - 1; // first column holds the weekday labels
  if (weekIndex < 0) return;

  const day = gridStart.add(weekIndex * 7 + row.sectionRowIndex, 'day').format('YYYY-MM-DD');
  if (!(day in currentSeries.value)) return;
  selectedDay.value = selectedDay.value === day ? null : day;
}

const selectedDayOrgRows = computed<[string, number][]>(() => {
  const day = selectedDay.value;
  if (day === null) return [];
  return (data.value?.daily_activity_by_org ?? [])
    .map((org): [string, number] => [org.organisation_name, metricSeries(org)[day] ?? 0])
    .filter(([, value]) => value > 0)
    .sort(([, a], [, b]) => b - a);
});

const dailyActivityByOrg = computed<{ days: string[]; series: StackedSeries[] }>(() => {
  const orgs = data.value?.daily_activity_by_org ?? [];

  const days = Array.from(
    new Set([
      ...Object.keys(currentSeries.value),
      ...orgs.flatMap(org => Object.keys(metricSeries(org))),
    ])
  ).sort();
  const dayIndex = new Map(days.map((day, index) => [day, index]));

  const series: StackedSeries[] = orgs
    .map(org => {
      const values = Array.from({ length: days.length }, () => 0);
      let total = 0;
      for (const [day, value] of Object.entries(metricSeries(org))) {
        const dayIdx = dayIndex.get(day);
        if (dayIdx !== undefined) values[dayIdx] = value;
        total += value;
      }
      return { name: org.organisation_name, values, total };
    })
    .sort((a, b) => b.total - a.total)
    .map(({ name, values }, index) => ({
      name,
      values,
      color: categoricalColor(index),
    }));

  return { days, series };
});

// Compact axis labels ("Jul 15") — the ISO day strings are long enough to
// clutter the axis. The year is only spelled out when the range spans two.
const dayAxisLabels = computed<string[]>(() => {
  const days = dailyActivityByOrg.value.days;
  const years = new Set(days.map(day => day.slice(0, 4)));
  const format = years.size > 1 ? 'MMM D, YYYY' : 'MMM D';
  return days.map(day => dayjs(day).format(format));
});

async function fetchStatistics(): Promise<void> {
  try {
    const response = await getUserActivityStatistics({
      query: {
        included_org_uids: props.includedOrgUids,
        excluded_org_uids: props.excludedOrgUids,
        min_points: minPoints.value,
        ...buildStatisticsDateQuery(props.dateRange),
      },
    });
    if (response.response?.status === 200) {
      data.value = response.data;
    }
  } catch (error) {
    message.error('Failed to load user activity statistics');
    logger.error('Failed to load user activity statistics:', error);
  }
}

watch([props, minPoints], fetchStatistics, { deep: true, immediate: true });

// A refetch or metric switch can drop the zoomed day's data — release the
// zoom when the day disappears from the active series.
watch(currentSeries, series => {
  if (selectedDay.value !== null && !(selectedDay.value in series)) {
    selectedDay.value = null;
  }
});
</script>

<template>
  <n-space v-if="data !== undefined" vertical size="large">
    <n-space align="center" justify="space-between">
      <gaia-segmented v-model:value="metric" :options="metricOptions" />
      <n-space align="center" size="small">
        <n-text>Active User Threshold</n-text>
        <gaia-select
          v-model:value="minPoints"
          :options="minPointsOptions"
          size="small"
          style="width: 240px"
        />
      </n-space>
    </n-space>

    <n-card :title="`Daily ${metricLabel}`">
      <div class="heatmap-wrapper" @click="onHeatmapClick">
        <n-heatmap :data="heatmapData" color-theme="orange" :tooltip="{ delay: 0 }">
          <template #tooltip="{ timestamp }">
            {{ heatmapTooltip(timestamp) }}
          </template>
          <template #footer>
            <n-text depth="3">Click a day to zoom into its organisation breakdown.</n-text>
          </template>
        </n-heatmap>
      </div>
    </n-card>

    <n-space v-if="selectedDay !== null" vertical size="small">
      <n-space align="center" size="small">
        <n-text depth="3">Zoomed into</n-text>
        <n-tag closable size="small" @close="selectedDay = null">{{ selectedDay }}</n-tag>
      </n-space>
      <statistics-bar-chart-card
        :title="`${metricLabel} by Organisation — ${selectedDay}`"
        :rows="selectedDayOrgRows"
        horizontal
        :x-label="metricLabel"
        y-label="Organisation"
        :format-value="metricValueFormatter"
        :value-ticks="metric === 'peak_points' ? peakValueTicks : undefined"
        empty-description="No organisation activity on this day"
      />
    </n-space>

    <statistics-stacked-bar-chart-card
      v-else
      :title="`Daily ${metricLabel} by Organisation`"
      :categories="dayAxisLabels"
      :series="dailyActivityByOrg.series"
      x-label="Day"
      :y-label="metricLabel"
      :format-value="metricValueFormatter"
      empty-description="No activity in the selected range"
    />
  </n-space>
</template>

<style scoped>
/* NHeatmap offers no click interaction, scroll handling, or alignment of its
   own: the pointer cursor signals the rects are clickable (see onHeatmapClick),
   and the wrapper centers the year grid in the card while still scrolling
   horizontally on narrow screens ("safe" keeps the left edge reachable). */
.heatmap-wrapper {
  display: flex;
  justify-content: safe center;
  overflow-x: auto;
}

.heatmap-wrapper :deep(.n-heatmap-rect) {
  cursor: pointer;
}
</style>
